/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * DateTextField
 *
 * @file DateTextField.jsx
 * @author Satyaki Bose
 */

import React from 'react';
import { shift, offset } from '@floating-ui/react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { GlobalStyles } from '@mui/material';
import { parseDate, formatDateToStorage, formatMonthYear } from '../../common/dateTimeUtils';
import {
  AssignmentFormCustomHeader,
  AssignmentFormDatePickerPortalWrapper,
  AssignmentFormMonthYearLabel,
  AssignmentFormNavButton,
  AssignmentFormNavGroup,
  CalendarIcon,
  CustomHeader,
  DateInput,
  DateInputError,
  DatePickerContainer,
  DatePickerPortalGlobalStyles,
  DatePickerPortalWrapper,
  datePickerPopperClassName,
  DateWrapper,
  Label,
  MonthYearLabel,
  NavButton
} from './Styles/AetDateFieldStyle';
import Icon from '../icons/Icon';
import { QUICK_SCHEDULE_FORM_DATE_FIELD_CLASS } from '../QuickSchedule/quickScheduleFormConstants';

const DateTextField = ({
  label,
  value,
  onChange,
  parentApp,
  icons,
  hasError,
  className,
  minDate,
  maxDate,
  filterDate,
  placeholderText,
  dateFormat,
  hasSuccess
}) => {
  const usesAssignmentFormCalendar = (
    className === 'assignment-form-date-field'
    || className === QUICK_SCHEDULE_FORM_DATE_FIELD_CLASS
  );

  const popperContainer = React.useCallback(
    ({ children }) => (
      usesAssignmentFormCalendar
        ? <AssignmentFormDatePickerPortalWrapper>{children}</AssignmentFormDatePickerPortalWrapper>
        : <DatePickerPortalWrapper>{children}</DatePickerPortalWrapper>
    ),
    [usesAssignmentFormCalendar]
  );

  if (!label) return null;

  if (parentApp && parentApp.toLowerCase() !== 'aet') {
    return null;
  }

  const handleDateChange = (date) => {
    const formattedDate = formatDateToStorage(date);
    onChange({ target: { value: formattedDate } });
  };

  const selectedDate = parseDate(value);
  const openToDate = selectedDate || minDate || undefined;
  const InputComponent = hasError ? DateInputError : DateInput;

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    decreaseYear,
    increaseYear,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    prevYearButtonDisabled,
    nextYearButtonDisabled
  }) => {
    if (usesAssignmentFormCalendar) {
      return (
        <AssignmentFormCustomHeader>
          <AssignmentFormNavGroup>
            <AssignmentFormNavButton
              type="button"
              onClick={decreaseYear}
              aria-label="Previous year"
              disabled={prevYearButtonDisabled}
            >
              <Icon name="CaretDoublePrevIcon" />
            </AssignmentFormNavButton>
            <AssignmentFormNavButton
              type="button"
              onClick={decreaseMonth}
              aria-label="Previous month"
              disabled={prevMonthButtonDisabled}
            >
              <Icon name="CaretPrevIcon" />
            </AssignmentFormNavButton>
          </AssignmentFormNavGroup>
          <AssignmentFormMonthYearLabel>
            {formatMonthYear(date)}
          </AssignmentFormMonthYearLabel>
          <AssignmentFormNavGroup>
            <AssignmentFormNavButton
              type="button"
              onClick={increaseMonth}
              aria-label="Next month"
              disabled={nextMonthButtonDisabled}
            >
              <Icon name="CaretNextIcon" />
            </AssignmentFormNavButton>
            <AssignmentFormNavButton
              type="button"
              onClick={increaseYear}
              aria-label="Next year"
              disabled={nextYearButtonDisabled}
            >
              <Icon name="CaretDoubleNextIcon" />
            </AssignmentFormNavButton>
          </AssignmentFormNavGroup>
        </AssignmentFormCustomHeader>
      );
    }

    return (
      <CustomHeader>
        <NavButton
          type="button"
          onClick={decreaseYear}
          aria-label="Previous year"
          disabled={prevYearButtonDisabled}
        >
          <Icon name="CaretDoublePrevIcon" />
        </NavButton>
        <NavButton
          type="button"
          onClick={decreaseMonth}
          aria-label="Previous month"
          disabled={prevMonthButtonDisabled}
        >
          <Icon name="CaretPrevIcon" />
        </NavButton>
        <MonthYearLabel>
          {formatMonthYear(date)}
        </MonthYearLabel>
        <NavButton
          type="button"
          onClick={increaseMonth}
          aria-label="Next month"
          disabled={nextMonthButtonDisabled}
        >
          <Icon name="CaretNextIcon" />
        </NavButton>
        <NavButton
          type="button"
          onClick={increaseYear}
          aria-label="Next year"
          disabled={nextYearButtonDisabled}
        >
          <Icon name="CaretDoubleNextIcon" />
        </NavButton>
      </CustomHeader>
    );
  };

  return (
    <>
      <GlobalStyles styles={DatePickerPortalGlobalStyles} />
      <DateWrapper className={className}>
        <Label>{label}</Label>
        <DatePickerContainer>
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            dateFormat={dateFormat || 'MM/dd/yy'}
            placeholderText={placeholderText || 'DD/MM/YY'}
            customInput={<InputComponent hasSuccess={hasSuccess} />}
            showPopperArrow={false}
            popperPlacement="bottom-start"
            popperProps={{
              middleware: [
                offset(8),
                shift({
                  padding: 10,
                  boundary: document.body,
                  crossAxis: true
                })
              ]
            }}
            popperClassName={datePickerPopperClassName}
            portalId="react-datepicker-portal"
            popperContainer={popperContainer}
            openToDate={openToDate}
            calendarStartDay={0}
            renderCustomHeader={renderCustomHeader}
            minDate={minDate || new Date()}
            maxDate={maxDate || undefined}
            filterDate={filterDate || null}
          />
          {icons?.calendar && (
            <CalendarIcon
              onClick={() => {}}
              type="button"
              aria-label="Calendar icon"
            >
              {icons.calendar}
            </CalendarIcon>
          )}
        </DatePickerContainer>
      </DateWrapper>
    </>
  );
};

DateTextField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  parentApp: PropTypes.string.isRequired,
  icons: PropTypes.object,
  hasError: PropTypes.bool,
  className: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  /** Latest selectable calendar day; used by assisted schedule end-date bounds. */
  maxDate: PropTypes.instanceOf(Date),
  filterDate: PropTypes.func,
  placeholderText: PropTypes.string,
  dateFormat: PropTypes.string,
  hasSuccess: PropTypes.bool
};

DateTextField.defaultProps = {
  label: '',
  icons: null,
  hasError: false,
  hasSuccess: false,
  className: '',
  minDate: null,
  maxDate: null,
  filterDate: null,
  placeholderText: '',
  dateFormat: ''
};

export default DateTextField;
